import { useAuth0 } from "@auth0/auth0-react";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Popover,
  SelectChangeEvent,
  Stack,
  Tabs,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { CompareArrows, InfoRounded, Output } from "@mui/icons-material";
import { lighten, useTheme } from "@mui/material/styles";

import { refreshDescription, updateMeta } from "@/api";
import {
  ButtonIcon,
  HelpBox,
  SegmentBox,
  ViewTab,
  useActivePage,
} from "@/components/atoms";
import { PageContainer } from "@/components/containers";
import { InsightLayout } from "@/components/layouts";
import {
  AttitudesView,
  AudienceMiniStats,
  CausesView,
  DemographicsView,
  DigitalView,
  InterestsView,
  MapView,
  MediaView,
  MindsetsView,
  MovementView,
  PenPortraitView,
  SiteView,
  SpendView,
  StatementsView,
  TopScoreView,
  TvView,
} from "@/components/molecules";
import {
  InsightMetaProvider,
  useAudienceContext
} from "@/context";
import { useInsight } from "@/hooks/api";
import { AudienceSegment, BuildAudience, Insight, TabOption } from "@/types";
import {
  checkAudience,
  checkPermission,
  defaultSegment,
  downloadColumns,
  downloadFile,
  filterTabs,
  getAudienceBreadcrumb,
  getClientComponent,
  getFirstWord,
  getInsightScores,
  getSelectionSummary,
  insightTabOptions,
  prepareData,
} from "@/utils";
import { InsightSkeleton } from "./InsightSkeleton";

const InsightPage: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { dispatch } = useAudienceContext();

  let { audienceId } = useParams<{ audienceId: string }>();
  const { audience, insight, loading, error } = useInsight(audienceId);

  const theme = useTheme();

  const navigate = useNavigate();
  const { user } = useAuth0();
  const showInsight = (user && audience) ? (checkPermission(user, "insight") && checkAudience(audience)) : false;
  const showMap = user ? checkPermission(user, "map") : false;
  const allowCompare = audience && audience.audience_type === 'build' && audience.status === "complete"

  const [segment, setSegment] = useState<AudienceSegment>(defaultSegment);
  const [segmentNum, setSegmentNum] = useState<number>(0);

  const [tabs, setTabs] = useState<TabOption[]>([]);
  const [currentTab, setCurrentTab] = useState<TabOption | undefined>(
    undefined
  );
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const ClientDemographicsView = getClientComponent(
    "DemographicsView",
    DemographicsView
  );

  useEffect(() => {
    audience &&
      audience.segments.length > 0 &&
      setSegment(audience.segments[0]);
  }, [audience]);

  useEffect(() => {
    if (audience && audience.segments.length > 0) {
      if (segment.segment_id !== 0) {
        setSegmentNum(
          audience.segments.map((x) => x.segment_id).indexOf(segment.segment_id)
        );
      } else {
        setSegmentNum(0);
      }
    }
  }, [audience, segment]);

  useEffect(() => {
    if (user) {
      if (showInsight) {
        setTabs(filterTabs(insightTabOptions, user));
      } else {
        // If the user doesn't have access to insight we just show the pen portrait (and maybe the map)
        if (showMap) {
          setTabs([insightTabOptions[0], insightTabOptions[1]]);
        } else {
          setTabs([insightTabOptions[0]]);
        }
      }
      setCurrentTab(insightTabOptions[0]);
    }
  }, [showInsight, user]);

  const handleUpdateDescription = async (text: string) => {
    const accessToken = await getAccessTokenSilently();

    if (!insight) return;
    if (!audienceId) return;

    let newMeta = [...insight.meta];
    newMeta[segmentNum].description_short = text;

    const { data, error } = await updateMeta(accessToken, audienceId, newMeta);

    if (data) {
      dispatch({
        type: "CHANGE_INSIGHT_META",
        payload: data.summary,
      });
    }

    if (error) {
      // TODO - Snackbar
      console.log(error);
    }
  };

  const handleRefreshDescription = async () => {
    const accessToken = await getAccessTokenSilently();

    if (!insight) return;
    if (!audienceId) return;

    const { data, error } = await refreshDescription(
      accessToken,
      audienceId,
      insight.insights[segmentNum],
      insight.pen_portraits[segmentNum]
    );

    if (data) {
      return data.description;
    }

    if (error) {
      // TODO - Snackbar
      console.log(error);
    }
  };

  const handleSegmentSelect = (event: SelectChangeEvent) => {
    setSegment(
      audience?.segments.find(
        (option) => option.segment_name === event.target.value
      ) || defaultSegment
    );
  };

  const getTabNumber = (tab: TabOption | undefined): number => {
    return tab ? tabs.findIndex((x) => x.name === tab.name) : 0;
  };

  const handleTabChange = (_event: React.SyntheticEvent, newTab: number) => {
    setCurrentTab(tabs[newTab]);
  };

  const handleDownload = useCallback(
    (tab: TabOption) => {
      if (!insight) return;

    // Prepare data
    const data = getInsightScores(tab, insight.insights[segmentNum], insight.mindsets[segmentNum]);

      if (data.length > 0) {
        // Build download
        const csv = prepareData(data, downloadColumns);

        // Download data
        const filename = `${audience!.audience_name} - ${tab.label}.csv`;
        downloadFile(csv, filename);

      // TODO Snackbar
      } else {
        console.warn(`Unable to get data for: ${tab.name}`);

        // TODO Snackbar
        return;
      }
    },
    [audience, insight, segmentNum]
  );

  const handleCompare = () => {
    if (audience && insight) {
      navigate("/compare", {state: { audience, insight }});
    }
  };

  const actionButtons = (
    <Stack direction="row" spacing={2}>
      {allowCompare && (
        <ButtonIcon
          onClick={handleCompare}
          color="primary"
          size={38}
          Icon={CompareArrows}
          tooltip="Compare with another Audience"
        />
      )}
      <Button
        variant="contained"
        size="small"
        disableElevation
        startIcon={<Output />}
        onClick={() => navigate("../export", { relative: "path" })}
      >
        Export
      </Button>
    </Stack>
  );

  const renderInsightView = (insight: Insight, tab: TabOption | undefined) => {
    if (tab) {
      if (tab.name === "pen_portrait") {
        return (
          <InsightMetaProvider
            handleUpdateDescription={handleUpdateDescription}
            handleRefreshDescription={handleRefreshDescription}
          >
            <PenPortraitView
              meta={insight.meta[segmentNum]}
              insights={insight.insights[segmentNum]}
              pen_portrait={insight.pen_portraits[segmentNum]}
              locations={insight.locations[segmentNum]}
              mindsets={insight.mindsets[segmentNum]}
              sites={insight.sites && insight.sites[segmentNum]}
              editable={!insight.sites}
            />
          </InsightMetaProvider>
        );
      } else if (tab.name === "map") {
        return <MapView locations={insight.locations[segmentNum]} />;
      } else if (tab.name === "sites") {
        return <SiteView locations={insight.locations[segmentNum]} scores={insight.sites && insight.sites[segmentNum]} />;
      } else if (tab.name === "demographics") {
        return (
          <ClientDemographicsView
            demographics={insight.insights[segmentNum].filter(
              (x) => x.tag_type === "Demographics"
            )}
          />
        );
      } else if (tab.name === "interests") {
        return (
          <InterestsView
            scores={insight.insights[segmentNum].filter(
              (x) => x.tag_type === "Interests"
            )}
          />
        );
      } else if (tab.name === "causes") {
        return (
          <CausesView
            scores={insight.insights[segmentNum].filter(
              (x) => x.tag_type === "Causes"
            )}
          />
        );
      } else if (tab.name === "spend") {
        return (
          <SpendView
            categories={insight.insights[segmentNum].filter(
              (x) => x.tag_type === "Spend Categories"
            )}
            brands={insight.insights[segmentNum].filter(
              (x) => x.tag_type === "Spend Merchants"
            )}
          />
        );
      } else if (tab.name === "media") {
        return (
          <MediaView
            scores={insight.insights[segmentNum].filter(
              (x) => x.tag_type === "Media"
            )}
            brandScores={insight.insights[segmentNum].filter(
              (x) =>
                x.tag_type === "Spend Merchants" &&
                x.tag_category === "Media Subscriptions"
            )}
          />
        );
      } else if (tab.name === "attitudes") {
        return (
          <AttitudesView
            scores={insight.insights[segmentNum].filter(
              (x) => x.tag_type === "Attitudes"
            )}
          />
        );
      } else if (tab.name === "digital") {
        return (
          <DigitalView
            scores={insight.insights[segmentNum].filter(
              (x) => x.tag_type === "Digital Consumption"
            )}
          />
        );
      } else if (tab.name === "statements") {
        return (
          <StatementsView
            scores={insight.insights[segmentNum].filter(
              (x) => x.tag_type === "Lifestyle Statements"
            )}
          />
        );
      } else if (tab.name === "movement") {
        return (
          <MovementView
            scores={insight.insights[segmentNum].filter(
              (x) => x.tag_type === "Movement"
            )}
          />
        );
      } else if (tab.name === "tv") {
        return (
          <TvView
            scores={insight.insights[segmentNum].filter(
              (x) => x.tag_type === "TV"
            )}
            lkp={insight.tv}
          />
        );
      } else if (tab.name === "top scoring") {
        return <TopScoreView data={insight.insights[segmentNum]} />;
      } else if (tab.name === "mindsets") {
        return <MindsetsView data={insight.mindsets[segmentNum]} />;
      } else {
        return null;
      }
    }
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const renderAudienceDefinition = (audience: BuildAudience) => {
    const summaryText = getSelectionSummary(audience.var_groups, audience.operator)

    return (
      <Typography variant="body2">
        <Typography variant="body2" component="span">
          {getFirstWord(summaryText) === 'interested' ? "People who are " : "People who "}
        </Typography>
        {summaryText && Array.isArray(summaryText) ? (
          summaryText
        ) : (
          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 'bold' }}>
            {summaryText}
          </Typography>
        )}
      </Typography>
    );
  };

  return (
    <PageContainer actions={actionButtons}>
      {loading ? (
        <InsightSkeleton />
      ) : (
        <Grid container spacing={2} flexDirection="column" flexGrow={1} pb={8}>
          {error && (
            <Grid>
              <Alert variant="filled" severity="error">
                {error}
              </Alert>
            </Grid>
          )}
          {audience && (
            <>
              {/* Audience Name and Summary */}
              <Grid
                size={12}
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="h6">
                    {audience.audience_name || "New Audience"}
                  </Typography>
                  {audience.project_name && (
                    <Chip size="small" label={audience.project_name} />
                  )}
                  {audience.audience_type === "build" && (
                    <>
                      <Typography
                        aria-owns={open ? "mouse-over-popover" : undefined}
                        aria-haspopup="true"
                        color="primary"
                        onMouseEnter={handlePopoverOpen}
                        onMouseLeave={handlePopoverClose}
                      >
                        <InfoRounded fontSize="small" sx={{ mt: 1 }} />
                      </Typography>
                      <Popover
                        id="mouse-over-popover"
                        sx={{ pointerEvents: "none" }}
                        open={open}
                        anchorEl={anchorEl}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        onClose={handlePopoverClose}
                        disableRestoreFocus
                      >
                        <Box sx={{ p: 2, maxWidth: 400 }}>
                          <Typography variant="body1" mb={1}>
                            Your Audience
                          </Typography>
                          {renderAudienceDefinition(audience)}
                        </Box>
                      </Popover>
                    </>
                  )}
                  {audience.segments.length > 0 && (
                    <SegmentBox
                      label={"Segment"}
                      value={segment.segment_name}
                      options={audience.segments}
                      onChange={handleSegmentSelect}
                      showSelected={true}
                    />
                  )}
                </Stack>
                {audience.audience_size && (
                  <AudienceMiniStats
                    audienceSize={audience.audience_size[segmentNum]}
                  />
                )}
              </Grid>
              {(insight && (showInsight || showMap)) && (
                <Grid
                  size={12}
                  mb={2}
                  sx={{ display: "flex", justifyContent: "center" }}
                >
                  <Box
                    sx={{
                      maxWidth: "calc(100dvw - 260px - 48px)",
                      minWidth: !showInsight ? undefined :  "720px",
                    }}
                  >
                    <Tabs
                      value={getTabNumber(currentTab)}
                      onChange={handleTabChange}
                      scrollButtons="auto"
                      variant="scrollable"
                      TabIndicatorProps={{ style: { display: "none" } }}
                      sx={{
                        ".MuiTabs-scrollButtons.Mui-disabled": { opacity: 0.3 },
                        padding: 1,
                        borderRadius: 2,
                        background: lighten(
                          theme.palette.background.paper,
                          0.1
                        ),
                        width: 1,
                        maxWidth: "100%",
                      }}
                    >
                      {tabs.map((tab, idx) => (
                        <ViewTab
                          label={
                            <HelpBox title={tab.description}>
                              <Typography color="inherit">{tab.label}</Typography>
                            </HelpBox>
                          }
                          key={tab.name}
                          icon={tab.icon}
                          iconPosition="start"
                        />
                      ))}
                    </Tabs>
                  </Box>
                </Grid>
              )}
            </>
          )}

          {/* Insight Views */}
          {(insight && currentTab) ? (
            <InsightLayout tab={currentTab} onDownload={handleDownload}>
              {renderInsightView(insight, currentTab)}
            </InsightLayout>
          ) : (
            audience && audience.status === 'processing' ? (
              <Typography
                variant="body1"
                color="textSecondary"
                sx={{
                  pt: 4,
                  textAlign: 'center'
                }}
              >
                The Insights for this audience are still being processed, please
                come back later or await a notification
              </Typography>
              ) : (
              <Typography
                variant="body1"
                color="textSecondary"
                sx={{
                  pt: 4,
                  textAlign: 'center'
                }}
              >
                No Insights are available for this audience, please go back and build them.
              </Typography>
            )
          )}
        </Grid>
      )}
    </PageContainer>
  );
};

export default InsightPage;
